import { useMemo, useState } from 'react'
import { ThemeProvider } from '@emotion/react'
import { Box, createTheme, Paper } from '@mui/material'
import { DataGrid } from '@mui/x-data-grid'
import CompliancePlip from '../../../../components/CompliancePlip'
import { Task, TaskLog } from '../types'
import { ComplianceStatus, TeamMember } from '../../../../types'
import { getTaskHistoryComplianceMessage, getTasksById, getUserFullName, getUsersById } from '../utils/helpers'
import dayjs from 'dayjs'
import { TableButton } from '../../../../components/Buttons.styled'
import AddressTaskModal from './AddressTaskModal'
import { complianceSortComparator } from '../../../../helpers/complianceSortComparator'
import { useAppSelector } from '../../../../redux/store'
import { selectLoadingState } from '../slice'

const tableTheme = createTheme({
  typography: {
    fontFamily: '"Open Sans", sans-serif',
    fontSize: 13
  },
  components: {
    MuiTableCell: {
      styleOverrides: {
        head: {
          fontWeight: 'bold'
        }
      }
    }
  }
})

interface TaskHistoryProps {
  users: TeamMember[]
  tasks: Task[] | null
  taskLogs: TaskLog[]
}

const Compliance = {
  'missed': ComplianceStatus.red,
  'complete': ComplianceStatus.green,
  'skipped': ComplianceStatus.gray
}

const TaskHistory = ({ taskLogs, users, tasks }: TaskHistoryProps) => {
  const [isAddressTaskModalOpen, setIsAddressTaskModalOpen] = useState(false)
  const [selectedTaskLog, setSelectedTaskLog] = useState<TaskLog | null>(null)

  const loadingState = useAppSelector(selectLoadingState)
  const isLoading = loadingState === 'loading'

  const handleAddressTaskModal = (open: boolean, taskLog: TaskLog | null) => () => {
    setSelectedTaskLog(taskLog)
    setIsAddressTaskModalOpen(open)
  }

  const usersById: Map<number, TeamMember> = useMemo(() => {
    return getUsersById(users)
  }, [users])

  const tasksById: Map<number, Task> = useMemo(() => {
    return getTasksById(tasks)
  }, [tasks])  

  return (
    <Box>
      <Paper>
        <ThemeProvider theme={tableTheme}>
          <DataGrid
            sx={{ borderLeft: 'none', borderRight: 'none', borderBottom: 'none', borderRadius: '0' }}
            rows={taskLogs}
            columns={[
              {
                field: 'compliance',
                headerName: '',
                sortable: true,
                width: 90,
                renderCell: (params) => (
                  <CompliancePlip message={getTaskHistoryComplianceMessage(params.row.status)} status={Compliance[params.row.status]} />
                ),
                align: 'center',
                type: "number",
                valueGetter: (value, row) => {
                  return Compliance[row.status] ?? 0
                },
                getSortComparator: (sortDirection) => {
                  return (a, b, cellParams1, cellParams2) => complianceSortComparator(sortDirection, a, b, cellParams1, cellParams2)
                }
              },
              {
                field: 'status',
                headerName: 'Status',
                flex: 1,
                minWidth: 150,
                valueGetter: (value, row) => {
                  return getTaskHistoryComplianceMessage(row.status)
                },                
                renderCell: (params) => (
                  <>{getTaskHistoryComplianceMessage(params.row.status)}</>
                ),
              },
              {
                field: 'assignedTo',
                headerName: 'User',
                flex: 1,
                minWidth: 150,
                renderCell: params => {
                  const userId = Number(params.row.userId)
                  const assignedTo = getUserFullName(userId, usersById)

                  return <>{ assignedTo }</>
                },
                type: "string",
                valueGetter: (value, row) => {
                  const userId = Number(row.userId)                
                  const assignedTo = getUserFullName(userId, usersById)

                  return assignedTo
                },
              },
              {
                field: 'assignedBy',
                headerName: 'Assigned By',
                flex: 1,
                minWidth: 150,
                type: "string",
                valueGetter: (value, row) => {
                  const task = tasksById.get(row.taskId)
                  const userId = Number(task?.assignedBy ?? null)
                  const assignedBy = getUserFullName(userId, usersById)

                  return assignedBy
                },
              },
              {
                field: 'title',
                headerName: 'Title',
                flex: 1,
                minWidth: 150,
                valueGetter: (value, row) => {
                  const task = tasksById.get(row.taskId)
                  const title = task?.name ?? ''
                  return title
                }
              },
              {
                field: 'date',
                headerName: 'Due Date',
                flex: 1,
                minWidth: 150,
                renderCell: (params) => (
                  <>{params.row.date ? dayjs(params.row.date).format('MM/DD/YYYY') : ''}</>
                ),
                type: "date",
                valueGetter: (value) => {
                  return new Date(value)
                },
              },
              {
                field: 'updatedDate',
                headerName: 'Last Updated',
                flex: 1,
                minWidth: 150,
                renderCell: (params) => (
                  <>{params.row.updatedDate ? dayjs(params.row.updatedDate).format('MM/DD/YYYY') : ''}</>
                ),
                type: "date",
                valueGetter: (value) => {
                  return new Date(value)
                }
              },
              {
                field: 'reminder_attempts',
                headerName: 'Reminders',
                flex: 1,
                minWidth: 150
              },
              {
                field: 'note',
                headerName: 'Corrective Action',
                flex: 1,
                minWidth: 150
              },
              {
                field: 'address',
                headerName: '',
                flex: 1,
                minWidth: 150,
                disableReorder: true,
                renderCell: (params) => (
                  <>
                    {params.row.status === 'missed' && (
                      <TableButton onClick={handleAddressTaskModal(true, params.row)} variant='contained'>
                        Mark As Addressed
                      </TableButton>
                    )}
                  </>
                )
              }
            ]}
            initialState={{
              pagination: {
                paginationModel: {
                  pageSize: 10,
                },
              },
              sorting: {
                sortModel: [
                  {
                    field: 'updatedDate',
                    sort: 'desc'
                  }
                ]
              }
            }}
            pageSizeOptions={[5, 10, 20, 25, 50, 100]}
            loading={isLoading}
            autoHeight
            density='compact'
          />
        </ThemeProvider>
      </Paper>
      {
        isAddressTaskModalOpen && selectedTaskLog && (
          <AddressTaskModal
            open={isAddressTaskModalOpen}
            onClose={handleAddressTaskModal(false, null)}
            taskLog={selectedTaskLog}
          />
        )
      }
    </Box>
  )
}

export default TaskHistory